import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Paper,
  Stack,
  TextField,
  InputLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

import { __API_URL__ } from "../../SERVER_URL";
import useVisitVideoTypes from "../../hooks/useVisitVideoTypes";
import { USER_ROLES } from "../../utils/constants";
import CustomButton from "../UI/CustomButton";
import SaveForFollowingVisitsCheckbox, {
  canSaveForFollowingVisits,
} from "./SaveForFollowingVisitsCheckbox";
import useUpdateVisitVideosMutation from "../../hooks/useUpdateVisitVideosMutation";
import useGetVisitVideos from "../../hooks/useGetVisitVideos";

const VideosModule = ({
  role,
  selectedSessionId,
  selectedVisit,
  patientId,
  visitId,
  step,
  disabled = false,
  showSelectAll = true,
  headerless = false,
  manageTestId,
  isTrialLocked = false,
}) => {
  const token = localStorage.getItem("token");

  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);

  // Local dialog state — tracks pending changes before Save is clicked
  const [localSelectedVideos, setLocalSelectedVideos] = useState([]);
  const [localIsAllVideos, setLocalIsAllVideos] = useState(false);
  const [localDisallowInv, setLocalDisallowInv] = useState(false);
  const [localApplyToFollowing, setLocalApplyToFollowing] = useState(false);

  // Add new video type state (unchanged — independent direct API call)
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const stepId = step?.id;
  const label = step?.title || "Videos";
  const testIdSuffix = stepId ? `-${stepId}` : "";

  const { data: videoTypes = [], refetch } = useVisitVideoTypes({
    visitId: patientId ? selectedVisit?.id : null,
  });

  const updateMutation = useUpdateVisitVideosMutation();

  // Step-scoped truth from the backend — always fetched (not just in patient mode) so a
  // repeated module's CRO view reads this step's own items instead of the flat, union-of-
  // all-steps `selectedVisit.app_videos` field.
  const { data: visitVideosData } = useGetVisitVideos({
    visitId,
    patientId,
    sessionId: selectedSessionId,
    stepId,
  });

  const isInvestigatorRelated =
    role === USER_ROLES.INVESTIGATOR ||
    role === USER_ROLES.SUB_INVESTIGATOR ||
    role === USER_ROLES.CRC;

  const isReadOnly =
    isTrialLocked ||
    role === USER_ROLES.REVIEWER ||
    (patientId && selectedVisit.visit_number === -1) ||
    (patientId && selectedVisit?.disallow_inv_videos_selection);

  // Adverse Event visits must always keep the investigator locked out of picking videos —
  // CRO can't turn this off.
  const isAdverseEventVisit = selectedVisit?.visit_number === -1;

  // Sync enabled state from server data. In patient mode, only an explicit session
  // selection counts as "on" — items merely inherited from the visit are not a real choice yet.
  // Exception: when the investigator is locked out of picking (disallow_inv), there will
  // never be an explicit session selection, so the visit's forced items count as "on" instead.
  useEffect(() => {
    if (!patientId) {
      if (visitVideosData?.app_videos?.length) {
        setEnabled(true);
      }
    } else if (
      (visitVideosData?.is_session_selection || visitVideosData?.disallow_inv_videos_selection) &&
      visitVideosData?.app_videos?.length
    ) {
      setEnabled(true);
    }
  }, [patientId, visitVideosData]);

  // Initialise local dialog flags from this step's visit-level truth each time the dialog opens
  useEffect(() => {
    if (open) {
      setLocalIsAllVideos(!!visitVideosData?.is_all_videos);
      setLocalDisallowInv(isAdverseEventVisit || !!visitVideosData?.disallow_inv_videos_selection);
      setLocalApplyToFollowing(!!selectedVisit?.apply_videos_to_following);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate the investigator's selection — only when the session has an explicit
  // override; otherwise the visit's items are just inherited, not actually chosen yet
  useEffect(() => {
    if (open) {
      if (!patientId) {
        setLocalSelectedVideos(visitVideosData?.app_videos || []);
      } else {
        setLocalSelectedVideos(
          visitVideosData?.is_session_selection || visitVideosData?.disallow_inv_videos_selection
            ? visitVideosData?.app_videos || []
            : []
        );
      }
    }
  }, [open, patientId, visitVideosData]); // eslint-disable-line react-hooks/exhaustive-deps

  const localSelectedIds = useMemo(
    () => localSelectedVideos.map((v) => v.id),
    [localSelectedVideos]
  );

  const allSelected = videoTypes.length > 0 && localSelectedIds.length === videoTypes.length;

  const handleToggleEnabled = (e) => {
    const checked = e.target.checked;
    setEnabled(checked);

    if (!checked) {
      updateMutation.mutate({
        visitId,
        patientId,
        sessionId: selectedSessionId,
        stepId,
        appVideos: [],
        isAllVideos: false,
        disallowInvVideosSelection: false,
        applyVideosToFollowing: false,
      });
    }
  };

  const toggleVideo = (video) => {
    if (isReadOnly) return;

    setLocalSelectedVideos((prev) => {
      const exists = prev.some((v) => v.id === video.id);
      return exists ? prev.filter((v) => v.id !== video.id) : [...prev, video];
    });
  };

  // Add new video type — direct API call, independent of the module save
  const handleAddVideoType = async () => {
    if (!title || !description.trim() || !image) {
      toast.error("Please fill all fields");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("image", image);

    setSaving(true);

    try {
      await axios.post(`${__API_URL__}/api/photos/add_video_type/`, formData, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Video type added");
      setTitle("");
      setDescription("");
      setImage(null);
      setAdding(false);
      refetch();
    } catch (err) {
      toast.error("Failed to add video type");
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAll = () => {
    if (isReadOnly) return;
    setLocalSelectedVideos(allSelected ? [] : videoTypes);
  };

  const handleSaveForAllVisits = () => {
    setLocalIsAllVideos((prev) => !prev);
  };

  const handleDisableInvVideoSelection = () => {
    setLocalDisallowInv((prev) => !prev);
  };

  const handleSave = () => {
    updateMutation.mutate(
      {
        visitId,
        patientId,
        sessionId: selectedSessionId,
        stepId,
        appVideos: localSelectedVideos.map((v) => ({ label: v.label || v.title })),
        isAllVideos: localIsAllVideos,
        disallowInvVideosSelection: headerless ? false : localDisallowInv,
        applyVideosToFollowing: localApplyToFollowing,
      },
      { onSuccess: () => setOpen(false) }
    );
  };

  // Investigators are not shown modules the CRO did not configure for this visit
  if (
    isInvestigatorRelated &&
    patientId &&
    !headerless &&
    selectedVisit?.visit_number >= 0 &&
    visitVideosData?.visit_configured === false
  ) {
    return null;
  }

  return (
    <Box sx={headerless ? {} : { width: "100%" }}>
      {headerless ? (
        <CustomButton
          size="small"
          variant="outlined"
          onClick={() => setOpen(true)}
          data-testid={manageTestId}
        >
          {isReadOnly ? "View" : "Manage"}
        </CustomButton>
      ) : (
        /* Header */
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox
              checked={enabled}
              onChange={handleToggleEnabled}
              disabled={isReadOnly || disabled || updateMutation.isPending}
              data-testid={`videos-enable-checkbox${testIdSuffix}`}
              inputProps={{ "data-testid": `videos-enable-checkbox-input${testIdSuffix}` }}
            />

            <Typography variant="subtitle1" fontWeight={600}>
              {label}
            </Typography>
          </Stack>

          {enabled && (
            <Stack direction="row" spacing={1} alignItems="center">
              {patientId && (
                <Tooltip title="Go to Videos">
                  <IconButton
                    component={Link}
                    to={`/patient/${patientId}/video/${!!selectedSessionId ? selectedSessionId : selectedVisit.photo_session_id}/?visitName=${encodeURIComponent(selectedVisit.visit_name)}`}
                    target="_blank"
                    size="small"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <CustomButton
                size="small"
                variant="outlined"
                data-testid={`videos-manage-btn${testIdSuffix}`}
                onClick={() => setOpen(true)}
              >
                {isReadOnly ? "View" : "Manage"}
              </CustomButton>
            </Stack>
          )}
        </Stack>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isReadOnly ? "View" : "Manage"} {label}
        </DialogTitle>

        <DialogContent dividers>
          {/* CRO flags */}
          {!isReadOnly &&
            (role === USER_ROLES.CRO ||
              role === USER_ROLES.CRO_COORDINATOR ||
              role === USER_ROLES.DATA_MANAGER ||
              role === USER_ROLES.SUPER_ADMIN) && (
              <Box sx={{ mb: 1 }}>
                {!headerless && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={localDisallowInv}
                      onChange={handleDisableInvVideoSelection}
                      disabled={isAdverseEventVisit}
                    />
                    <Typography variant="body2">
                      Disable videos selection for investigator
                      {isAdverseEventVisit && " (always enabled for Adverse Event visits)"}
                    </Typography>
                  </Stack>
                )}

                {!patientId && canSaveForFollowingVisits(selectedVisit) && (
                  <SaveForFollowingVisitsCheckbox
                    label="Save videos configurations for all coming visits"
                    checked={localIsAllVideos}
                    onChange={handleSaveForAllVisits}
                  />
                )}
              </Box>
            )}

          {/* Select all */}
          {showSelectAll && !isReadOnly && (
            <Typography
              variant="body2"
              sx={{ cursor: "pointer", color: "primary.main", mb: 1 }}
              onClick={handleSelectAll}
            >
              {allSelected ? "Deselect All Videos: " : "Select All Videos: "}
              {localSelectedIds.length}
            </Typography>
          )}

          {/* Max info */}
          {isInvestigatorRelated && selectedVisit?.is_maximum_video_types && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Maximum Videos: {selectedVisit?.maximum_video_types}
            </Typography>
          )}

          {/* List */}
          <List>
            {videoTypes.map((video) => {
              const checked = localSelectedIds.includes(video.id);

              return (
                <ListItem
                  key={video.id}
                  disableGutters
                  sx={{ mb: 1 }}
                  data-video-label={video.label}
                >
                  <Paper
                    sx={{
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={isReadOnly}
                      onChange={() => toggleVideo(video)}
                    />
                    <ListItemText primary={video.label} />
                  </Paper>
                </ListItem>
              );
            })}
          </List>

          {/* Apply to following visits - Investigator/CRC */}
          {!isReadOnly && isInvestigatorRelated && patientId && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Checkbox
                checked={localApplyToFollowing}
                onChange={() => setLocalApplyToFollowing((prev) => !prev)}
              />
              <Typography variant="body2">Apply to all following visits</Typography>
            </Stack>
          )}

          {/* Add new video type (unchanged) */}
          {!isReadOnly && !isInvestigatorRelated && (
            <>
              {!adding ? (
                <Button fullWidth variant="outlined" sx={{ mt: 2 }} onClick={() => setAdding(true)}>
                  Add New Video Type
                </Button>
              ) : (
                <Stack spacing={2} mt={2}>
                  <TextField
                    label="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />

                  <TextField
                    label="Description"
                    multiline
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />

                  <Stack spacing={1}>
                    <InputLabel>Upload Image</InputLabel>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setImage(e.target.files[0])}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2}>
                    <Button variant="outlined" onClick={() => setAdding(false)}>
                      Cancel
                    </Button>
                    <Button variant="contained" onClick={handleAddVideoType} disabled={saving}>
                      {saving ? "Saving..." : "Save"}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          {!isReadOnly && (
            <Button variant="contained" onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VideosModule;
